import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext.jsx';
import {
  AVAILABLE_BILLING_LOCALES,
  DEFAULT_ACTIVE_BILLING_LOCALE,
  buildPremiumCopy,
} from '@/config/billing';
import { appConfigService } from '@/services/appConfigService';

const AppConfigContext = createContext({
  billingLocale: DEFAULT_ACTIVE_BILLING_LOCALE,
  billingConfig: null,
  premiumCopy: buildPremiumCopy(DEFAULT_ACTIVE_BILLING_LOCALE),
  availableBillingLocales: AVAILABLE_BILLING_LOCALES,
  loading: true,
  updatingBillingLocale: false,
  refreshConfig: async () => {},
  updateBillingLocale: async () => {},
});

export const AppConfigProvider = ({ children }) => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [billingLocale, setBillingLocale] = useState(DEFAULT_ACTIVE_BILLING_LOCALE);
  const [billingConfig, setBillingConfig] = useState({
    activeLocale: DEFAULT_ACTIVE_BILLING_LOCALE,
    availableLocales: AVAILABLE_BILLING_LOCALES.map((locale) => locale.locale),
    updatedAt: null,
    updatedBy: null,
  });
  const [loading, setLoading] = useState(true);
  const [updatingBillingLocale, setUpdatingBillingLocale] = useState(false);
  const [error, setError] = useState(null);

  const hydrateBillingConfig = useCallback((data) => {
    const nextLocale = data?.activeLocale || DEFAULT_ACTIVE_BILLING_LOCALE;
    setBillingLocale(nextLocale);
    setBillingConfig({
      activeLocale: nextLocale,
      availableLocales:
        data?.availableLocales?.length > 0
          ? data.availableLocales
          : AVAILABLE_BILLING_LOCALES.map((locale) => locale.locale),
      updatedAt: data?.updatedAt || null,
      updatedBy: data?.updatedBy || null,
    });
  }, []);

  const fetchBillingConfig = useCallback(async () => {
    if (!user) {
      hydrateBillingConfig(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await appConfigService.getBillingLocale();
      hydrateBillingConfig(data);
      setError(null);
    } catch (err) {
      console.warn('Failed to load billing config from backend, using defaults', err);
      // Use default config instead of showing error to user
      hydrateBillingConfig(null);
      setError(err);
      // Don't show toast error - just use defaults silently
      // toast.error('Failed to load billing configuration');
    } finally {
      setLoading(false);
    }
  }, [hydrateBillingConfig, user]);

  useEffect(() => {
    if (authLoading) return;
    fetchBillingConfig();
  }, [authLoading, fetchBillingConfig]);

  const handleUpdateBillingLocale = useCallback(
    async (locale) => {
      setUpdatingBillingLocale(true);
      try {
        const data = await appConfigService.updateBillingLocale(locale);
        hydrateBillingConfig(data);
        toast.success('Billing locale updated');
        return data;
      } catch (err) {
        console.error('Failed to update billing locale', err);
        const message = err?.message || err?.response?.data?.message || 'Failed to update billing locale';
        toast.error(message);
        throw err;
      } finally {
        setUpdatingBillingLocale(false);
      }
    },
    [hydrateBillingConfig]
  );

  const premiumCopy = useMemo(() => buildPremiumCopy(billingLocale), [billingLocale]);

  const value = {
    billingLocale,
    billingConfig,
    premiumCopy,
    availableBillingLocales: AVAILABLE_BILLING_LOCALES,
    loading,
    updatingBillingLocale,
    isAdmin,
    error,
    refreshConfig: fetchBillingConfig,
    updateBillingLocale: handleUpdateBillingLocale,
  };

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

export default AppConfigContext;
